import {
  buildLinesFromEvaluation,
  deriveEvaluatorInputFromLine,
} from '@xbcb/work-order-utils/dist/applyTariffEvaluation/utils';
import type {
  TariffEvaluatorInput,
  TariffEvaluatorOutput,
  TariffTransportInfoInput,
  UsConsumptionEntryLineInput,
  UsConsumptionEntryProductInput,
} from '@xbcb/api-gateway-client';

export type EvaluateTariffsFn = (
  input: TariffEvaluatorInput,
) => Promise<TariffEvaluatorOutput | null | undefined>;

export interface ApplyTariffEvaluationToFormLineArgs {
  line: UsConsumptionEntryLineInput;
  product: UsConsumptionEntryProductInput;
  transport?: TariffTransportInfoInput;
  evaluate: EvaluateTariffsFn;
}

export const applyTariffEvaluationToFormLine = async ({
  line,
  product,
  transport,
  evaluate,
}: ApplyTariffEvaluationToFormLineArgs): Promise<
  UsConsumptionEntryLineInput[] | undefined
> => {
  const input = deriveEvaluatorInputFromLine(line, product, transport);
  if (!input) return undefined;

  // The shared util spreads net weight without checking for a value, so a
  // partial `{ unit }` entry from the form will fail the evaluator's
  // `Float!` requirement on `netWeight.value`. Drop it when value is missing.
  if (input.netWeight && input.netWeight.value == null) {
    delete (input as { netWeight?: unknown }).netWeight;
  }

  let evaluation: TariffEvaluatorOutput | null | undefined;
  try {
    evaluation = await evaluate(input);
  } catch {
    return undefined;
  }
  if (!evaluation) return undefined;

  return buildLinesFromEvaluation(line, evaluation);
};

export interface ApplyTariffEvaluationToFormProductArgs {
  product: UsConsumptionEntryProductInput;
  transport?: TariffTransportInfoInput;
  evaluate: EvaluateTariffsFn;
}

export const applyTariffEvaluationToFormProduct = async ({
  product,
  transport,
  evaluate,
}: ApplyTariffEvaluationToFormProductArgs): Promise<UsConsumptionEntryProductInput> => {
  if (!product.lines?.length) return { ...product };

  const updatedLineGroups = await Promise.all(
    product.lines.map((line) =>
      applyTariffEvaluationToFormLine({
        line,
        product,
        transport,
        evaluate,
      }),
    ),
  );

  const lines = product.lines.flatMap((originalLine, idx) => {
    const newLines = updatedLineGroups[idx];
    return newLines?.length ? newLines : [originalLine];
  });

  return { ...product, lines };
};
